import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { useAdSurfaceAndSlot } from '../../hooks/useAdSurfaceAndSlot';
import { isValidAdDestination } from '../../utils/adDestinationUrl';
import {
  PopupWrap,
  PopupImageWrap,
  PopupImage,
  CloseBtn,
  SponsoredTag,
  PopupBody,
  PopupTitle,
  PopupDesc,
  PopupActions,
  CtaButton,
  GhostBtn,
} from './AdPopup.styled';

export default function AdPopup({ ad, delay = 8000, pageKey, onVisible, onCtaClick }) {
  const { loaded, slotVisible } = useAdSurfaceAndSlot(pageKey);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!loaded || !slotVisible || !ad?.id) return undefined;
    const t = setTimeout(() => {
      try {
        const key = `ad-popup-${ad.id}-dismissed`;
        if (!sessionStorage.getItem(key)) setVisible(true);
      } catch {
        setVisible(true);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [ad?.id, delay, loaded, slotVisible]);

  useEffect(() => {
    if (!visible || !onVisible || !ad?.managedCampaignId) return;
    onVisible(ad);
  }, [visible, onVisible, ad?.managedCampaignId, ad]);

  const dismiss = () => {
    setDismissed(true);
    setVisible(false);
    try {
      if (ad?.id) sessionStorage.setItem(`ad-popup-${ad.id}-dismissed`, 'true');
    } catch {
      /* ignore */
    }
  };

  if (!loaded || !slotVisible || dismissed || !ad?.image || !visible) return null;

  const dest = isValidAdDestination(ad.url);
  const ctaHref = dest ? ad.url : '#';

  return (
    <PopupWrap role="dialog" aria-label="Sponsored">
      <PopupImageWrap>
        <PopupImage src={ad.image} alt={ad.title} loading="lazy" />
        <CloseBtn type="button" onClick={dismiss} aria-label="Close ad">
          ×
        </CloseBtn>
        <SponsoredTag>Sponsored</SponsoredTag>
      </PopupImageWrap>
      <PopupBody>
        <PopupTitle>{ad.title}</PopupTitle>
        <PopupDesc>{ad.description}</PopupDesc>
        <PopupActions>
          <CtaButton
            href={ctaHref}
            target={dest ? '_blank' : undefined}
            rel={dest ? 'noopener noreferrer sponsored' : undefined}
            onClick={(e) => {
              if (!dest) e.preventDefault();
              else onCtaClick?.(e, ad);
            }}
          >
            {ad.cta} <ExternalLink size={12} />
          </CtaButton>
          <GhostBtn type="button" onClick={dismiss}>
            No thanks
          </GhostBtn>
        </PopupActions>
      </PopupBody>
    </PopupWrap>
  );
}
